(() => {
  const STORAGE_KEY = 'makema:swatch-sort-mode';
  const NUMBER_MODE = 'number';
  const TONE_MODE = 'tone';
  const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

  const readMode = () => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === TONE_MODE ? TONE_MODE : NUMBER_MODE;
    } catch (_error) {
      return NUMBER_MODE;
    }
  };

  const storeMode = (mode) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, mode);
    } catch (_error) {
      // Die Sortierung funktioniert auch, wenn der Browser Session Storage blockiert.
    }
  };

  const parseHex = (value) => {
    const match = value.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (!match) return null;
    const hex = match[1].length === 3
      ? match[1].split('').map((character) => character + character).join('')
      : match[1];
    return [0, 2, 4].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
  };

  const rgbToHsl = ([red, green, blue]) => {
    const maximum = Math.max(red, green, blue);
    const minimum = Math.min(red, green, blue);
    const lightness = (maximum + minimum) / 2;
    const difference = maximum - minimum;
    if (difference === 0) return { hue: 0, saturation: 0, lightness, chroma: 0 };

    const saturation = difference / (1 - Math.abs(2 * lightness - 1));
    let hue;
    if (maximum === red) hue = 60 * (((green - blue) / difference) % 6);
    else if (maximum === green) hue = 60 * (((blue - red) / difference) + 2);
    else hue = 60 * (((red - green) / difference) + 4);
    if (hue < 0) hue += 360;
    return { hue, saturation, lightness, chroma: difference };
  };

  const classifyTone = (listItem) => {
    const label = listItem.querySelector('.color-swatch');
    if (!label) return { group: 12, lightness: 0, hue: 0 };

    const image = label.style.getPropertyValue('--swatch-background-image').trim();
    if (image && image !== 'none') return { group: 11, lightness: 0, hue: 0 };

    const rgb = parseHex(label.style.getPropertyValue('--swatch-background'));
    if (!rgb) return { group: 12, lightness: 0, hue: 0 };
    const { hue, saturation, lightness, chroma } = rgbToHsl(rgb);

    const isOpticalWhite = lightness >= 0.88 && chroma <= 0.08;
    if (isOpticalWhite || (lightness >= 0.78 && saturation <= 0.28)) return { group: 0, lightness, hue };
    if (lightness <= 0.16 && saturation <= 0.24) return { group: 10, lightness, hue };
    if (hue >= 18 && hue < 48 && lightness <= 0.48) return { group: 8, lightness, hue };
    const isMutedGreen = hue >= 54 && hue < 172 && saturation >= 0.10;
    const isMutedBlue = hue >= 172 && hue < 258 && saturation >= 0.08;
    if (saturation <= 0.16 && !isMutedGreen && !isMutedBlue) return { group: 9, lightness, hue };
    const isBrightNeonYellow = hue >= 54 && hue < 72 && saturation >= 0.90 && lightness >= 0.48;
    if ((hue >= 48 && hue < 54) || isBrightNeonYellow) return { group: 1, lightness, hue };
    if (hue >= 18 && hue < 48) return { group: 2, lightness, hue };
    if (hue < 18 || hue >= 348) return { group: 3, lightness, hue };
    if (hue >= 320) return { group: 4, lightness, hue };
    if (hue >= 258) return { group: 5, lightness, hue };
    if (hue >= 172) return { group: 6, lightness, hue };
    if (hue >= 54) return { group: 7, lightness, hue };
    return { group: 12, lightness, hue };
  };

  const optionValue = (listItem) => listItem.querySelector('input[data-option-value]')?.dataset.optionValue || '';

  const applyMode = (picker, mode) => {
    picker.querySelectorAll('[data-makema-swatch-sort]').forEach((controls) => {
      const list = controls.nextElementSibling;
      if (!list?.classList.contains('swatches')) return;
      const items = Array.from(list.children);

      items.sort((left, right) => {
        const leftValue = optionValue(left);
        const rightValue = optionValue(right);
        if (mode === NUMBER_MODE) return collator.compare(leftValue, rightValue);

        const leftTone = classifyTone(left);
        const rightTone = classifyTone(right);
        return leftTone.group - rightTone.group
          || rightTone.lightness - leftTone.lightness
          || leftTone.hue - rightTone.hue
          || collator.compare(leftValue, rightValue);
      });

      const orderChanged = items.some((item, index) => list.children[index] !== item);
      if (orderChanged) items.forEach((item) => list.appendChild(item));
      controls.querySelectorAll('button[data-swatch-sort-mode]').forEach((button) => {
        const active = button.dataset.swatchSortMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    });
  };

  const initialise = (root = document) => {
    root.querySelectorAll('variant-picker[data-makema-yarn-swatches]').forEach((picker) => {
      if (picker.dataset.makemaSwatchSortReady === 'true') return;
      picker.dataset.makemaSwatchSortReady = 'true';
      applyMode(picker, readMode());
    });
  };

  let refreshScheduled = false;
  const refreshAfterThemeUpdate = () => {
    if (refreshScheduled) return;
    refreshScheduled = true;
    requestAnimationFrame(() => {
      refreshScheduled = false;
      const mode = readMode();
      document.querySelectorAll('variant-picker[data-makema-yarn-swatches]').forEach((picker) => applyMode(picker, mode));
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-swatch-sort-mode]');
    if (!button) return;
    const picker = button.closest('variant-picker[data-makema-yarn-swatches]');
    if (!picker) return;
    const mode = button.dataset.swatchSortMode === TONE_MODE ? TONE_MODE : NUMBER_MODE;
    storeMode(mode);
    document.querySelectorAll('variant-picker[data-makema-yarn-swatches]').forEach((element) => applyMode(element, mode));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
  else initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  new MutationObserver(refreshAfterThemeUpdate).observe(document.documentElement, { childList: true, subtree: true });
})();